// Tipos de permissões por ação
export interface ModulePermissions {
  read: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
}

// Níveis de acesso predefinidos
export enum AccessLevel {
  NONE = 'none',
  READ_ONLY = 'read_only',
  READ_CREATE = 'read_create',
  READ_UPDATE = 'read_update',
  READ_CREATE_UPDATE = 'read_create_update',
  FULL = 'full'
}

// Mapear nível de acesso para permissões
export const accessLevelToPermissions = (level: AccessLevel): ModulePermissions => {
  switch (level) {
    case AccessLevel.NONE:
      return { read: false, create: false, update: false, delete: false };
    case AccessLevel.READ_ONLY:
      return { read: true, create: false, update: false, delete: false };
    case AccessLevel.READ_CREATE:
      return { read: true, create: true, update: false, delete: false };
    case AccessLevel.READ_UPDATE:
      return { read: true, create: false, update: true, delete: false };
    case AccessLevel.READ_CREATE_UPDATE:
      return { read: true, create: true, update: true, delete: false };
    case AccessLevel.FULL:
      return { read: true, create: true, update: true, delete: true };
    default:
      return { read: false, create: false, update: false, delete: false };
  }
};

// Converter permissões para nível de acesso
export const permissionsToAccessLevel = (perms: ModulePermissions | undefined): AccessLevel => {
  // Tratamento defensivo: se permissões não existirem, retornar NONE
  if (!perms) return AccessLevel.NONE;

  if (!perms.read) return AccessLevel.NONE;
  if (perms.read && perms.create && perms.update && perms.delete) return AccessLevel.FULL;
  if (perms.read && perms.create && perms.update) return AccessLevel.READ_CREATE_UPDATE;
  if (perms.read && perms.update) return AccessLevel.READ_UPDATE;
  if (perms.read && perms.create) return AccessLevel.READ_CREATE;
  if (perms.read) return AccessLevel.READ_ONLY;
  return AccessLevel.NONE;
};

// Módulos do sistema
export enum SystemModule {
  DASHBOARD = 'dashboard',
  VEICULOS = 'veiculos',
  MOTORISTAS = 'motoristas',
  EQUIPAMENTOS = 'equipamentos',
  OBRAS = 'obras',
  CHECKLISTS = 'checklists',
  ITENS_CHECKLIST = 'itens_checklist',
  GRUPOS_CHECKLIST = 'grupos_checklist',
  TRABALHOS = 'trabalhos',
  OCORRENCIAS = 'ocorrencias',
  AGENDAMENTOS = 'agendamentos',
  ABASTECIMENTOS = 'abastecimentos',
  PRODUTIVIDADE = 'produtividade',
  MANUTENCOES_PREVENTIVAS = 'manutencoes_preventivas',
  MANUTENCOES_CORRETIVAS = 'manutencoes_corretivas',
  PNEUS = 'pneus',
  MULTAS = 'multas',
  SINISTROS = 'sinistros',
  ESTOQUE = 'estoque',
  COMPRAS = 'compras',
  TACOGRAFOS = 'tacografos',
  DASHBOARD_COMBUSTIVEL = 'dashboard_combustivel',
  SCORING_MOTORISTAS = 'scoring_motoristas',
  TELEMATICA = 'telematica',
  RODIZIO_RECAPAGEM = 'rodizio_recapagem',
  COMPLIANCE = 'compliance',
  TCO = 'tco',
  RELATORIOS = 'relatorios',
  RELATORIO_PRODUTIVIDADE_EQUIPAMENTOS = 'relatorio_produtividade_equipamentos',
  RELATORIO_CHECKLISTS = 'relatorio_checklists',
  RELATORIO_VEICULOS = 'relatorio_veiculos',
  RELATORIO_OCORRENCIAS = 'relatorio_ocorrencias',
  AUTOMACAO = 'automacao',
  AUTOMACAO_EDITOR = 'automacao_editor',
  AUTOMACAO_SIMULADOR = 'automacao_simulador',
  CONFERENCIA_CARGA = 'conferencia_carga',
  CONFERENCIA_DOCUMENTOS = 'conferencia_documentos',
  CONFERENCIA_DIVERGENCIAS = 'conferencia_divergencias',
  CONFERENCIA_RELATORIOS = 'conferencia_relatorios',
  CONFIGURACOES = 'configuracoes',
  CONFIG_SISTEMA = 'config_sistema',
  CONFIG_BACKUP = 'config_backup',
  CONFIG_INTEGRACOES = 'config_integracoes',
  CONFIG_WHATSAPP = 'config_whatsapp',
  USUARIOS = 'usuarios'
}

// Estrutura completa de permissões de usuário
export interface UserPermissions {
  id?: number;
  usuario_id: number;

  // Permissões por módulo
  perm_dashboard: ModulePermissions;
  perm_veiculos: ModulePermissions;
  perm_motoristas: ModulePermissions;
  perm_equipamentos: ModulePermissions;
  perm_obras: ModulePermissions;
  perm_checklists: ModulePermissions;
  perm_itens_checklist: ModulePermissions;
  perm_grupos_checklist: ModulePermissions;
  perm_trabalhos: ModulePermissions;
  perm_ocorrencias: ModulePermissions;
  perm_agendamentos: ModulePermissions;
  perm_abastecimentos: ModulePermissions;
  perm_produtividade: ModulePermissions;
  perm_manutencoes_preventivas: ModulePermissions;
  perm_manutencoes_corretivas: ModulePermissions;
  perm_pneus: ModulePermissions;
  perm_multas: ModulePermissions;
  perm_sinistros: ModulePermissions;
  perm_estoque: ModulePermissions;
  perm_compras: ModulePermissions;
  perm_tacografos: ModulePermissions;
  perm_dashboard_combustivel: ModulePermissions;
  perm_scoring_motoristas: ModulePermissions;
  perm_telematica: ModulePermissions;
  perm_rodizio_recapagem: ModulePermissions;
  perm_compliance: ModulePermissions;
  perm_tco: ModulePermissions;
  perm_relatorios: ModulePermissions;
  perm_relatorio_produtividade_equipamentos: ModulePermissions;
  perm_relatorio_checklists: ModulePermissions;
  perm_relatorio_veiculos: ModulePermissions;
  perm_relatorio_ocorrencias: ModulePermissions;
  perm_automacao: ModulePermissions;
  perm_automacao_editor: ModulePermissions;
  perm_automacao_simulador: ModulePermissions;
  perm_conferencia_carga: ModulePermissions;
  perm_conferencia_documentos: ModulePermissions;
  perm_conferencia_divergencias: ModulePermissions;
  perm_conferencia_relatorios: ModulePermissions;
  perm_configuracoes: ModulePermissions;
  perm_config_sistema: ModulePermissions;
  perm_config_backup: ModulePermissions;
  perm_config_integracoes: ModulePermissions;
  perm_config_whatsapp: ModulePermissions;
  perm_usuarios: ModulePermissions;

  // Controle de acesso (visibilidade do módulo)
  acesso_dashboard: boolean;
  acesso_veiculos: boolean;
  acesso_motoristas: boolean;
  acesso_equipamentos: boolean;
  acesso_obras: boolean;
  acesso_checklists: boolean;
  acesso_itens_checklist: boolean;
  acesso_grupos_checklist: boolean;
  acesso_trabalhos: boolean;
  acesso_ocorrencias: boolean;
  acesso_agendamentos: boolean;
  acesso_abastecimentos: boolean;
  acesso_produtividade: boolean;
  acesso_manutencoes_preventivas: boolean;
  acesso_manutencoes_corretivas: boolean;
  acesso_pneus: boolean;
  acesso_multas: boolean;
  acesso_sinistros: boolean;
  acesso_estoque: boolean;
  acesso_compras: boolean;
  acesso_tacografos: boolean;
  acesso_dashboard_combustivel: boolean;
  acesso_scoring_motoristas: boolean;
  acesso_telematica: boolean;
  acesso_rodizio_recapagem: boolean;
  acesso_compliance: boolean;
  acesso_tco: boolean;
  acesso_relatorios: boolean;
  acesso_relatorio_produtividade_equipamentos: boolean;
  acesso_relatorio_checklists: boolean;
  acesso_relatorio_veiculos: boolean;
  acesso_relatorio_ocorrencias: boolean;
  acesso_automacao: boolean;
  acesso_automacao_editor: boolean;
  acesso_automacao_simulador: boolean;
  acesso_conferencia_carga: boolean;
  acesso_conferencia_documentos: boolean;
  acesso_conferencia_divergencias: boolean;
  acesso_conferencia_relatorios: boolean;
  acesso_configuracoes: boolean;
  acesso_config_sistema: boolean;
  acesso_config_backup: boolean;
  acesso_config_integracoes: boolean;
  acesso_config_whatsapp: boolean;
  acesso_usuarios: boolean;

  criado_em?: string;
  atualizado_em?: string;
}

// Configuração de módulo para exibição
export interface ModuleConfig {
  key: SystemModule;
  label: string;
  description: string;
  permKey: keyof Pick<UserPermissions,
    'perm_dashboard' | 'perm_veiculos' | 'perm_motoristas' | 'perm_equipamentos' |
    'perm_obras' | 'perm_checklists' | 'perm_itens_checklist' | 'perm_grupos_checklist' |
    'perm_trabalhos' | 'perm_ocorrencias' | 'perm_agendamentos' | 'perm_abastecimentos' |
    'perm_produtividade' | 'perm_manutencoes_preventivas' | 'perm_manutencoes_corretivas' |
    'perm_pneus' | 'perm_multas' | 'perm_sinistros' | 'perm_estoque' | 'perm_compras' |
    'perm_tacografos' | 'perm_dashboard_combustivel' | 'perm_scoring_motoristas' |
    'perm_telematica' | 'perm_rodizio_recapagem' |
    'perm_compliance' | 'perm_tco' |
    'perm_relatorios' | 'perm_relatorio_produtividade_equipamentos' | 'perm_relatorio_checklists' |
    'perm_relatorio_veiculos' | 'perm_relatorio_ocorrencias' | 'perm_automacao' |
    'perm_automacao_editor' | 'perm_automacao_simulador' |
    'perm_conferencia_carga' | 'perm_conferencia_documentos' |
    'perm_conferencia_divergencias' | 'perm_conferencia_relatorios' | 'perm_configuracoes' |
    'perm_config_sistema' | 'perm_config_backup' | 'perm_config_integracoes' |
    'perm_config_whatsapp' | 'perm_usuarios'
  >;
  accessKey: keyof Pick<UserPermissions,
    'acesso_dashboard' | 'acesso_veiculos' | 'acesso_motoristas' | 'acesso_equipamentos' |
    'acesso_obras' | 'acesso_checklists' | 'acesso_itens_checklist' | 'acesso_grupos_checklist' |
    'acesso_trabalhos' | 'acesso_ocorrencias' | 'acesso_agendamentos' | 'acesso_abastecimentos' |
    'acesso_produtividade' | 'acesso_manutencoes_preventivas' | 'acesso_manutencoes_corretivas' |
    'acesso_pneus' | 'acesso_multas' | 'acesso_sinistros' | 'acesso_estoque' | 'acesso_compras' |
    'acesso_tacografos' | 'acesso_dashboard_combustivel' | 'acesso_scoring_motoristas' |
    'acesso_telematica' | 'acesso_rodizio_recapagem' |
    'acesso_compliance' | 'acesso_tco' |
    'acesso_relatorios' | 'acesso_relatorio_produtividade_equipamentos' | 'acesso_relatorio_checklists' |
    'acesso_relatorio_veiculos' | 'acesso_relatorio_ocorrencias' | 'acesso_automacao' |
    'acesso_automacao_editor' | 'acesso_automacao_simulador' |
    'acesso_conferencia_carga' | 'acesso_conferencia_documentos' |
    'acesso_conferencia_divergencias' | 'acesso_conferencia_relatorios' | 'acesso_configuracoes' |
    'acesso_config_sistema' | 'acesso_config_backup' | 'acesso_config_integracoes' |
    'acesso_config_whatsapp' | 'acesso_usuarios'
  >;
  icon?: string;
  requireAdmin?: boolean;
  /** Se definido, este módulo de permissão só aparece quando o módulo da empresa está ativo */
  requiredEmpresaModule?: string;
}

// Lista de módulos do sistema
export const SYSTEM_MODULES: ModuleConfig[] = [
  {
    key: SystemModule.DASHBOARD,
    label: 'Dashboard',
    description: 'Visualização geral e métricas',
    permKey: 'perm_dashboard',
    accessKey: 'acesso_dashboard',
    icon: 'LayoutDashboard'
  },
  {
    key: SystemModule.VEICULOS,
    label: 'Veículos',
    description: 'Cadastro e gestão de veículos',
    permKey: 'perm_veiculos',
    accessKey: 'acesso_veiculos',
    icon: 'Truck'
  },
  {
    key: SystemModule.MOTORISTAS,
    label: 'Motoristas',
    description: 'Cadastro e gestão de motoristas',
    permKey: 'perm_motoristas',
    accessKey: 'acesso_motoristas',
    icon: 'Users'
  },
  {
    key: SystemModule.EQUIPAMENTOS,
    label: 'Equipamentos',
    description: 'Cadastro e gestão de equipamentos',
    permKey: 'perm_equipamentos',
    accessKey: 'acesso_equipamentos',
    icon: 'Wrench'
  },
  {
    key: SystemModule.OBRAS,
    label: 'Obras',
    description: 'Cadastro e gestão de obras',
    permKey: 'perm_obras',
    accessKey: 'acesso_obras',
    icon: 'Building'
  },
  {
    key: SystemModule.CHECKLISTS,
    label: 'Checklists',
    description: 'Criação e gestão de checklists',
    permKey: 'perm_checklists',
    accessKey: 'acesso_checklists',
    icon: 'CheckSquare'
  },
  {
    key: SystemModule.ITENS_CHECKLIST,
    label: 'Itens de Checklist',
    description: 'Cadastro e gestão de itens de checklist',
    permKey: 'perm_itens_checklist',
    accessKey: 'acesso_itens_checklist',
    icon: 'ListCheck'
  },
  {
    key: SystemModule.GRUPOS_CHECKLIST,
    label: 'Grupos de Checklist',
    description: 'Organização de checklists em grupos',
    permKey: 'perm_grupos_checklist',
    accessKey: 'acesso_grupos_checklist',
    icon: 'FolderKanban'
  },
  {
    key: SystemModule.TRABALHOS,
    label: 'Trabalhos',
    description: 'Gestão de trabalhos e tarefas',
    permKey: 'perm_trabalhos',
    accessKey: 'acesso_trabalhos',
    icon: 'Briefcase'
  },
  {
    key: SystemModule.OCORRENCIAS,
    label: 'Ocorrências',
    description: 'Registro e acompanhamento de ocorrências',
    permKey: 'perm_ocorrencias',
    accessKey: 'acesso_ocorrencias',
    icon: 'AlertTriangle'
  },
  {
    key: SystemModule.AGENDAMENTOS,
    label: 'Agendamentos',
    description: 'Gestão de agendamentos e reservas',
    permKey: 'perm_agendamentos',
    accessKey: 'acesso_agendamentos',
    icon: 'Calendar'
  },
  {
    key: SystemModule.ABASTECIMENTOS,
    label: 'Abastecimentos',
    description: 'Controle de abastecimentos de veículos',
    permKey: 'perm_abastecimentos',
    accessKey: 'acesso_abastecimentos',
    icon: 'Fuel'
  },
  {
    key: SystemModule.PRODUTIVIDADE,
    label: 'Produtividade',
    description: 'Análise de produtividade de equipamentos',
    permKey: 'perm_produtividade',
    accessKey: 'acesso_produtividade',
    icon: 'TrendingUp'
  },
  {
    key: SystemModule.MANUTENCOES_PREVENTIVAS,
    label: 'Manutenções Preventivas',
    description: 'Planejamento e controle de manutenções preventivas',
    permKey: 'perm_manutencoes_preventivas',
    accessKey: 'acesso_manutencoes_preventivas',
    icon: 'Tool'
  },
  {
    key: SystemModule.MANUTENCOES_CORRETIVAS,
    label: 'Manutenções Corretivas',
    description: 'Registro e acompanhamento de manutenções corretivas',
    permKey: 'perm_manutencoes_corretivas',
    accessKey: 'acesso_manutencoes_corretivas',
    icon: 'Wrench'
  },
  {
    key: SystemModule.PNEUS,
    label: 'Pneus',
    description: 'Gestao de pneus, medicoes de sulco e previsibilidade',
    permKey: 'perm_pneus',
    accessKey: 'acesso_pneus',
    icon: 'Circle'
  },
  {
    key: SystemModule.RODIZIO_RECAPAGEM,
    label: 'Rodizio e Recapagem',
    description: 'Rodizio de pneus entre posicoes e controle de recapagens',
    permKey: 'perm_rodizio_recapagem',
    accessKey: 'acesso_rodizio_recapagem',
    icon: 'RefreshCw'
  },
  {
    key: SystemModule.MULTAS,
    label: 'Multas',
    description: 'Gestao de multas de transito, defesas e recursos',
    permKey: 'perm_multas',
    accessKey: 'acesso_multas',
    icon: 'AlertTriangle'
  },
  {
    key: SystemModule.SINISTROS,
    label: 'Sinistros',
    description: 'Registro e acompanhamento de sinistros e acidentes',
    permKey: 'perm_sinistros',
    accessKey: 'acesso_sinistros',
    icon: 'ShieldAlert'
  },
  {
    key: SystemModule.ESTOQUE,
    label: 'Estoque de Pecas',
    description: 'Catalogo de pecas, saldos e movimentacoes de estoque',
    permKey: 'perm_estoque',
    accessKey: 'acesso_estoque',
    icon: 'Package'
  },
  {
    key: SystemModule.COMPRAS,
    label: 'Compras e NF-e',
    description: 'Fornecedores, ordens de compra e importacao de notas fiscais',
    permKey: 'perm_compras',
    accessKey: 'acesso_compras',
    icon: 'ShoppingCart'
  },
  {
    key: SystemModule.TACOGRAFOS,
    label: 'Tacografos',
    description: 'Leituras de tacografo, analise de conducao e infracoes',
    permKey: 'perm_tacografos',
    accessKey: 'acesso_tacografos',
    icon: 'Gauge'
  },
  {
    key: SystemModule.DASHBOARD_COMBUSTIVEL,
    label: 'Dashboard Combustivel',
    description: 'Analise de consumo por veiculo, motorista e tipo de combustivel',
    permKey: 'perm_dashboard_combustivel',
    accessKey: 'acesso_dashboard_combustivel',
    icon: 'Fuel'
  },
  {
    key: SystemModule.SCORING_MOTORISTAS,
    label: 'Scoring de Motoristas',
    description: 'Pontuacao e ranking de motoristas por desempenho',
    permKey: 'perm_scoring_motoristas',
    accessKey: 'acesso_scoring_motoristas',
    icon: 'Star'
  },
  {
    key: SystemModule.TELEMATICA,
    label: 'Telematica',
    description: 'Rastreamento GPS, dispositivos, eventos e geocercas',
    permKey: 'perm_telematica',
    accessKey: 'acesso_telematica',
    icon: 'Satellite'
  },
  {
    key: SystemModule.COMPLIANCE,
    label: 'Compliance',
    description: 'Controle de vencimento de documentos (CRLV, seguro, CNH, ASO)',
    permKey: 'perm_compliance',
    accessKey: 'acesso_compliance',
    icon: 'Shield'
  },
  {
    key: SystemModule.TCO,
    label: 'TCO',
    description: 'Custo Total de Propriedade por veiculo',
    permKey: 'perm_tco',
    accessKey: 'acesso_tco',
    icon: 'DollarSign'
  },
  {
    key: SystemModule.RELATORIOS,
    label: 'Relatórios',
    description: 'Geração e visualização de relatórios',
    permKey: 'perm_relatorios',
    accessKey: 'acesso_relatorios',
    icon: 'FileText'
  },
  {
    key: SystemModule.RELATORIO_PRODUTIVIDADE_EQUIPAMENTOS,
    label: 'Relatório de Produtividade de Equipamentos',
    description: 'Relatório de análise de produtividade de equipamentos',
    permKey: 'perm_relatorio_produtividade_equipamentos',
    accessKey: 'acesso_relatorio_produtividade_equipamentos',
    icon: 'TrendingUp'
  },
  {
    key: SystemModule.RELATORIO_CHECKLISTS,
    label: 'Relatório de Checklists',
    description: 'Relatório de checklists realizados',
    permKey: 'perm_relatorio_checklists',
    accessKey: 'acesso_relatorio_checklists',
    icon: 'ClipboardList'
  },
  {
    key: SystemModule.RELATORIO_VEICULOS,
    label: 'Relatório de Veículos',
    description: 'Relatório de veículos e manutenções',
    permKey: 'perm_relatorio_veiculos',
    accessKey: 'acesso_relatorio_veiculos',
    icon: 'Truck'
  },
  {
    key: SystemModule.RELATORIO_OCORRENCIAS,
    label: 'Relatório de Ocorrências',
    description: 'Relatório de ocorrências registradas',
    permKey: 'perm_relatorio_ocorrencias',
    accessKey: 'acesso_relatorio_ocorrencias',
    icon: 'AlertCircle'
  },
  {
    key: SystemModule.AUTOMACAO,
    label: 'Automação',
    description: 'Configuração de automações e fluxos',
    permKey: 'perm_automacao',
    accessKey: 'acesso_automacao',
    icon: 'Workflow'
  },
  {
    key: SystemModule.AUTOMACAO_EDITOR,
    label: 'Editor de Fluxos',
    description: 'Editor para criar e gerenciar fluxos de automação',
    permKey: 'perm_automacao_editor',
    accessKey: 'acesso_automacao_editor',
    icon: 'GitBranch'
  },
  {
    key: SystemModule.AUTOMACAO_SIMULADOR,
    label: 'Simulador de Fluxos',
    description: 'Simulador para testar fluxos de automação',
    permKey: 'perm_automacao_simulador',
    accessKey: 'acesso_automacao_simulador',
    icon: 'Play'
  },
  {
    key: SystemModule.CONFERENCIA_CARGA,
    label: 'Conferência de Carga',
    description: 'Conferência de carregamento e descarregamento de mercadorias',
    permKey: 'perm_conferencia_carga',
    accessKey: 'acesso_conferencia_carga',
    icon: 'PackageCheck',
    requiredEmpresaModule: 'conferencia_carga'
  },
  {
    key: SystemModule.CONFERENCIA_DOCUMENTOS,
    label: 'Documentos de Conferência',
    description: 'Gestão de NFs, manifestos e romaneios de conferência',
    permKey: 'perm_conferencia_documentos',
    accessKey: 'acesso_conferencia_documentos',
    icon: 'FileStack',
    requiredEmpresaModule: 'conferencia_carga'
  },
  {
    key: SystemModule.CONFERENCIA_DIVERGENCIAS,
    label: 'Divergências',
    description: 'Gestão de divergências encontradas nas conferências',
    permKey: 'perm_conferencia_divergencias',
    accessKey: 'acesso_conferencia_divergencias',
    icon: 'AlertOctagon',
    requiredEmpresaModule: 'conferencia_carga'
  },
  {
    key: SystemModule.CONFERENCIA_RELATORIOS,
    label: 'Relatórios de Conferência',
    description: 'Relatórios e métricas de conferências de carga',
    permKey: 'perm_conferencia_relatorios',
    accessKey: 'acesso_conferencia_relatorios',
    icon: 'BarChart3',
    requiredEmpresaModule: 'conferencia_carga'
  },
  {
    key: SystemModule.CONFIGURACOES,
    label: 'Configurações',
    description: 'Configurações gerais do sistema',
    permKey: 'perm_configuracoes',
    accessKey: 'acesso_configuracoes',
    icon: 'Settings',
    requireAdmin: true
  },
  {
    key: SystemModule.CONFIG_SISTEMA,
    label: 'Configurações do Sistema',
    description: 'Configurações gerais da empresa e do sistema',
    permKey: 'perm_config_sistema',
    accessKey: 'acesso_config_sistema',
    icon: 'Cog',
    requireAdmin: true
  },
  {
    key: SystemModule.CONFIG_BACKUP,
    label: 'Backup e Restauração',
    description: 'Gerenciamento de backups e arquivos do sistema',
    permKey: 'perm_config_backup',
    accessKey: 'acesso_config_backup',
    icon: 'Database',
    requireAdmin: true
  },
  {
    key: SystemModule.CONFIG_INTEGRACOES,
    label: 'Integrações',
    description: 'Configuração de integrações com sistemas externos',
    permKey: 'perm_config_integracoes',
    accessKey: 'acesso_config_integracoes',
    icon: 'Plug',
    requireAdmin: true
  },
  {
    key: SystemModule.CONFIG_WHATSAPP,
    label: 'Conexões WhatsApp',
    description: 'Gerenciamento de conexões WhatsApp',
    permKey: 'perm_config_whatsapp',
    accessKey: 'acesso_config_whatsapp',
    icon: 'MessageCircle',
    requireAdmin: true
  },
  {
    key: SystemModule.USUARIOS,
    label: 'Usuários',
    description: 'Gerenciamento de usuários e permissões',
    permKey: 'perm_usuarios',
    accessKey: 'acesso_usuarios',
    icon: 'UserCog',
    requireAdmin: true
  }
];

// Permissões padrão para novos usuários (operador)
export const DEFAULT_PERMISSIONS: Omit<UserPermissions, 'id' | 'usuario_id' | 'criado_em' | 'atualizado_em'> = {
  perm_dashboard: { read: true, create: false, update: false, delete: false },
  perm_veiculos: { read: true, create: false, update: false, delete: false },
  perm_motoristas: { read: true, create: false, update: false, delete: false },
  perm_equipamentos: { read: true, create: false, update: false, delete: false },
  perm_obras: { read: true, create: false, update: false, delete: false },
  perm_checklists: { read: true, create: false, update: false, delete: false },
  perm_itens_checklist: { read: true, create: false, update: false, delete: false },
  perm_grupos_checklist: { read: true, create: false, update: false, delete: false },
  perm_trabalhos: { read: true, create: false, update: false, delete: false },
  perm_ocorrencias: { read: true, create: false, update: false, delete: false },
  perm_agendamentos: { read: true, create: false, update: false, delete: false },
  perm_abastecimentos: { read: true, create: false, update: false, delete: false },
  perm_produtividade: { read: true, create: false, update: false, delete: false },
  perm_manutencoes_preventivas: { read: true, create: false, update: false, delete: false },
  perm_manutencoes_corretivas: { read: true, create: false, update: false, delete: false },
  perm_pneus: { read: true, create: false, update: false, delete: false },
  perm_multas: { read: true, create: false, update: false, delete: false },
  perm_sinistros: { read: true, create: false, update: false, delete: false },
  perm_estoque: { read: true, create: false, update: false, delete: false },
  perm_compras: { read: true, create: false, update: false, delete: false },
  perm_tacografos: { read: true, create: false, update: false, delete: false },
  perm_dashboard_combustivel: { read: true, create: false, update: false, delete: false },
  perm_scoring_motoristas: { read: true, create: false, update: false, delete: false },
  perm_telematica: { read: true, create: false, update: false, delete: false },
  perm_rodizio_recapagem: { read: true, create: false, update: false, delete: false },
  perm_compliance: { read: true, create: false, update: false, delete: false },
  perm_tco: { read: true, create: false, update: false, delete: false },
  perm_relatorios: { read: true, create: false, update: false, delete: false },
  perm_relatorio_produtividade_equipamentos: { read: true, create: false, update: false, delete: false },
  perm_relatorio_checklists: { read: true, create: false, update: false, delete: false },
  perm_relatorio_veiculos: { read: true, create: false, update: false, delete: false },
  perm_relatorio_ocorrencias: { read: true, create: false, update: false, delete: false },
  perm_automacao: { read: true, create: true, update: true, delete: false },
  perm_automacao_editor: { read: true, create: true, update: true, delete: false },
  perm_automacao_simulador: { read: true, create: false, update: false, delete: false },
  perm_conferencia_carga: { read: true, create: true, update: true, delete: false },
  perm_conferencia_documentos: { read: true, create: false, update: false, delete: false },
  perm_conferencia_divergencias: { read: true, create: true, update: false, delete: false },
  perm_conferencia_relatorios: { read: true, create: false, update: false, delete: false },
  perm_configuracoes: { read: false, create: false, update: false, delete: false },
  perm_config_sistema: { read: false, create: false, update: false, delete: false },
  perm_config_backup: { read: false, create: false, update: false, delete: false },
  perm_config_integracoes: { read: false, create: false, update: false, delete: false },
  perm_config_whatsapp: { read: false, create: false, update: false, delete: false },
  perm_usuarios: { read: false, create: false, update: false, delete: false },

  acesso_dashboard: true,
  acesso_veiculos: true,
  acesso_motoristas: true,
  acesso_equipamentos: true,
  acesso_obras: true,
  acesso_checklists: true,
  acesso_itens_checklist: true,
  acesso_grupos_checklist: true,
  acesso_trabalhos: true,
  acesso_ocorrencias: true,
  acesso_agendamentos: true,
  acesso_abastecimentos: true,
  acesso_produtividade: true,
  acesso_manutencoes_preventivas: true,
  acesso_manutencoes_corretivas: true,
  acesso_pneus: true,
  acesso_multas: true,
  acesso_sinistros: true,
  acesso_estoque: true,
  acesso_compras: true,
  acesso_tacografos: true,
  acesso_dashboard_combustivel: true,
  acesso_scoring_motoristas: true,
  acesso_telematica: true,
  acesso_rodizio_recapagem: true,
  acesso_compliance: true,
  acesso_tco: true,
  acesso_relatorios: true,
  acesso_relatorio_produtividade_equipamentos: true,
  acesso_relatorio_checklists: true,
  acesso_relatorio_veiculos: true,
  acesso_relatorio_ocorrencias: true,
  acesso_automacao: true,
  acesso_automacao_editor: true,
  acesso_automacao_simulador: true,
  acesso_conferencia_carga: true,
  acesso_conferencia_documentos: true,
  acesso_conferencia_divergencias: true,
  acesso_conferencia_relatorios: true,
  acesso_configuracoes: false,
  acesso_config_sistema: false,
  acesso_config_backup: false,
  acesso_config_integracoes: false,
  acesso_config_whatsapp: false,
  acesso_usuarios: false
};

// Permissões de admin (controle total)
export const ADMIN_PERMISSIONS: Omit<UserPermissions, 'id' | 'usuario_id' | 'criado_em' | 'atualizado_em'> = {
  perm_dashboard: { read: true, create: true, update: true, delete: true },
  perm_veiculos: { read: true, create: true, update: true, delete: true },
  perm_motoristas: { read: true, create: true, update: true, delete: true },
  perm_equipamentos: { read: true, create: true, update: true, delete: true },
  perm_obras: { read: true, create: true, update: true, delete: true },
  perm_checklists: { read: true, create: true, update: true, delete: true },
  perm_itens_checklist: { read: true, create: true, update: true, delete: true },
  perm_grupos_checklist: { read: true, create: true, update: true, delete: true },
  perm_trabalhos: { read: true, create: true, update: true, delete: true },
  perm_ocorrencias: { read: true, create: true, update: true, delete: true },
  perm_agendamentos: { read: true, create: true, update: true, delete: true },
  perm_abastecimentos: { read: true, create: true, update: true, delete: true },
  perm_produtividade: { read: true, create: true, update: true, delete: true },
  perm_manutencoes_preventivas: { read: true, create: true, update: true, delete: true },
  perm_manutencoes_corretivas: { read: true, create: true, update: true, delete: true },
  perm_pneus: { read: true, create: true, update: true, delete: true },
  perm_multas: { read: true, create: true, update: true, delete: true },
  perm_sinistros: { read: true, create: true, update: true, delete: true },
  perm_estoque: { read: true, create: true, update: true, delete: true },
  perm_compras: { read: true, create: true, update: true, delete: true },
  perm_tacografos: { read: true, create: true, update: true, delete: true },
  perm_dashboard_combustivel: { read: true, create: true, update: true, delete: true },
  perm_scoring_motoristas: { read: true, create: true, update: true, delete: true },
  perm_telematica: { read: true, create: true, update: true, delete: true },
  perm_rodizio_recapagem: { read: true, create: true, update: true, delete: true },
  perm_compliance: { read: true, create: true, update: true, delete: true },
  perm_tco: { read: true, create: true, update: true, delete: true },
  perm_relatorios: { read: true, create: true, update: true, delete: true },
  perm_relatorio_produtividade_equipamentos: { read: true, create: true, update: true, delete: true },
  perm_relatorio_checklists: { read: true, create: true, update: true, delete: true },
  perm_relatorio_veiculos: { read: true, create: true, update: true, delete: true },
  perm_relatorio_ocorrencias: { read: true, create: true, update: true, delete: true },
  perm_automacao: { read: true, create: true, update: true, delete: true },
  perm_automacao_editor: { read: true, create: true, update: true, delete: true },
  perm_automacao_simulador: { read: true, create: true, update: true, delete: true },
  perm_conferencia_carga: { read: true, create: true, update: true, delete: true },
  perm_conferencia_documentos: { read: true, create: true, update: true, delete: true },
  perm_conferencia_divergencias: { read: true, create: true, update: true, delete: true },
  perm_conferencia_relatorios: { read: true, create: true, update: true, delete: true },
  perm_configuracoes: { read: true, create: true, update: true, delete: true },
  perm_config_sistema: { read: true, create: true, update: true, delete: true },
  perm_config_backup: { read: true, create: true, update: true, delete: true },
  perm_config_integracoes: { read: true, create: true, update: true, delete: true },
  perm_config_whatsapp: { read: true, create: true, update: true, delete: true },
  perm_usuarios: { read: true, create: true, update: true, delete: true },

  acesso_dashboard: true,
  acesso_veiculos: true,
  acesso_motoristas: true,
  acesso_equipamentos: true,
  acesso_obras: true,
  acesso_checklists: true,
  acesso_itens_checklist: true,
  acesso_grupos_checklist: true,
  acesso_trabalhos: true,
  acesso_ocorrencias: true,
  acesso_agendamentos: true,
  acesso_abastecimentos: true,
  acesso_produtividade: true,
  acesso_manutencoes_preventivas: true,
  acesso_manutencoes_corretivas: true,
  acesso_pneus: true,
  acesso_multas: true,
  acesso_sinistros: true,
  acesso_estoque: true,
  acesso_compras: true,
  acesso_tacografos: true,
  acesso_dashboard_combustivel: true,
  acesso_scoring_motoristas: true,
  acesso_telematica: true,
  acesso_rodizio_recapagem: true,
  acesso_compliance: true,
  acesso_tco: true,
  acesso_relatorios: true,
  acesso_relatorio_produtividade_equipamentos: true,
  acesso_relatorio_checklists: true,
  acesso_relatorio_veiculos: true,
  acesso_relatorio_ocorrencias: true,
  acesso_automacao: true,
  acesso_automacao_editor: true,
  acesso_automacao_simulador: true,
  acesso_conferencia_carga: true,
  acesso_conferencia_documentos: true,
  acesso_conferencia_divergencias: true,
  acesso_conferencia_relatorios: true,
  acesso_configuracoes: true,
  acesso_config_sistema: true,
  acesso_config_backup: true,
  acesso_config_integracoes: true,
  acesso_config_whatsapp: true,
  acesso_usuarios: true
};

// Permissões padrão para conferente (focado em conferência de carga)
export const CONFERENTE_PERMISSIONS: Omit<UserPermissions, 'id' | 'usuario_id' | 'criado_em' | 'atualizado_em'> = {
  perm_dashboard: { read: true, create: false, update: false, delete: false },
  perm_veiculos: { read: false, create: false, update: false, delete: false },
  perm_motoristas: { read: false, create: false, update: false, delete: false },
  perm_equipamentos: { read: false, create: false, update: false, delete: false },
  perm_obras: { read: false, create: false, update: false, delete: false },
  perm_checklists: { read: false, create: false, update: false, delete: false },
  perm_itens_checklist: { read: false, create: false, update: false, delete: false },
  perm_grupos_checklist: { read: false, create: false, update: false, delete: false },
  perm_trabalhos: { read: false, create: false, update: false, delete: false },
  perm_ocorrencias: { read: false, create: false, update: false, delete: false },
  perm_agendamentos: { read: false, create: false, update: false, delete: false },
  perm_abastecimentos: { read: false, create: false, update: false, delete: false },
  perm_produtividade: { read: false, create: false, update: false, delete: false },
  perm_manutencoes_preventivas: { read: false, create: false, update: false, delete: false },
  perm_manutencoes_corretivas: { read: false, create: false, update: false, delete: false },
  perm_pneus: { read: false, create: false, update: false, delete: false },
  perm_multas: { read: false, create: false, update: false, delete: false },
  perm_sinistros: { read: false, create: false, update: false, delete: false },
  perm_estoque: { read: false, create: false, update: false, delete: false },
  perm_compras: { read: false, create: false, update: false, delete: false },
  perm_tacografos: { read: false, create: false, update: false, delete: false },
  perm_dashboard_combustivel: { read: false, create: false, update: false, delete: false },
  perm_scoring_motoristas: { read: false, create: false, update: false, delete: false },
  perm_telematica: { read: false, create: false, update: false, delete: false },
  perm_rodizio_recapagem: { read: false, create: false, update: false, delete: false },
  perm_compliance: { read: false, create: false, update: false, delete: false },
  perm_tco: { read: false, create: false, update: false, delete: false },
  perm_relatorios: { read: false, create: false, update: false, delete: false },
  perm_relatorio_produtividade_equipamentos: { read: false, create: false, update: false, delete: false },
  perm_relatorio_checklists: { read: false, create: false, update: false, delete: false },
  perm_relatorio_veiculos: { read: false, create: false, update: false, delete: false },
  perm_relatorio_ocorrencias: { read: false, create: false, update: false, delete: false },
  perm_automacao: { read: false, create: false, update: false, delete: false },
  perm_automacao_editor: { read: false, create: false, update: false, delete: false },
  perm_automacao_simulador: { read: false, create: false, update: false, delete: false },
  perm_conferencia_carga: { read: true, create: true, update: true, delete: false },
  perm_conferencia_documentos: { read: true, create: true, update: false, delete: false },
  perm_conferencia_divergencias: { read: true, create: true, update: true, delete: false },
  perm_conferencia_relatorios: { read: true, create: false, update: false, delete: false },
  perm_configuracoes: { read: false, create: false, update: false, delete: false },
  perm_config_sistema: { read: false, create: false, update: false, delete: false },
  perm_config_backup: { read: false, create: false, update: false, delete: false },
  perm_config_integracoes: { read: false, create: false, update: false, delete: false },
  perm_config_whatsapp: { read: false, create: false, update: false, delete: false },
  perm_usuarios: { read: false, create: false, update: false, delete: false },

  acesso_dashboard: true,
  acesso_veiculos: false,
  acesso_motoristas: false,
  acesso_equipamentos: false,
  acesso_obras: false,
  acesso_checklists: false,
  acesso_itens_checklist: false,
  acesso_grupos_checklist: false,
  acesso_trabalhos: false,
  acesso_ocorrencias: false,
  acesso_agendamentos: false,
  acesso_abastecimentos: false,
  acesso_produtividade: false,
  acesso_manutencoes_preventivas: false,
  acesso_manutencoes_corretivas: false,
  acesso_pneus: false,
  acesso_multas: false,
  acesso_sinistros: false,
  acesso_estoque: false,
  acesso_compras: false,
  acesso_tacografos: false,
  acesso_dashboard_combustivel: false,
  acesso_scoring_motoristas: false,
  acesso_telematica: false,
  acesso_rodizio_recapagem: false,
  acesso_compliance: false,
  acesso_tco: false,
  acesso_relatorios: false,
  acesso_relatorio_produtividade_equipamentos: false,
  acesso_relatorio_checklists: false,
  acesso_relatorio_veiculos: false,
  acesso_relatorio_ocorrencias: false,
  acesso_automacao: false,
  acesso_automacao_editor: false,
  acesso_automacao_simulador: false,
  acesso_conferencia_carga: true,
  acesso_conferencia_documentos: true,
  acesso_conferencia_divergencias: true,
  acesso_conferencia_relatorios: true,
  acesso_configuracoes: false,
  acesso_config_sistema: false,
  acesso_config_backup: false,
  acesso_config_integracoes: false,
  acesso_config_whatsapp: false,
  acesso_usuarios: false
};
